import nacl from 'tweetnacl';
import { base58Decode, base58Encode } from '../lib/base58';

/**
 * Wallet abstraction for the playtest client. The backend proves ownership with an
 * off-chain ed25519 signature over a server-issued challenge (auth/signature.ts):
 * both the address and signature cross the wire as base58.
 *
 *  - {@link PhantomWallet}: the real path — Phantom signs the challenge bytes.
 *  - {@link DevKeypairWallet}: a DEV-ONLY throwaway ed25519 keypair (persisted in
 *    localStorage) that signs with the SAME real crypto, so the backend verifies it
 *    exactly like any wallet. It is gated to `import.meta.env.DEV` at the call site
 *    and is not reachable in production builds. It is not a "funded" wallet, so it
 *    will classify `under`/`unverified` for the token gate unless the dev mock
 *    balance is enabled server-side.
 */
export type WalletKind = 'phantom' | 'dev';

export interface Wallet {
  readonly kind: WalletKind;
  readonly address: string;
  /** Sign the UTF-8 message; resolves to a base58 signature. */
  signMessage(message: string): Promise<string>;
}

// ── Pure ed25519 helpers (testable without a browser) ─────────────────────────

/** Sign a UTF-8 message with a 64-byte secret key; returns a base58 signature. */
export function signMessageEd25519(message: string, secretKey: Uint8Array): string {
  const bytes = new TextEncoder().encode(message);
  return base58Encode(nacl.sign.detached(bytes, secretKey));
}

/** Derive the base58 address (public key) from a keypair secret key. */
export function deriveAddress(secretKey: Uint8Array): string {
  return base58Encode(nacl.sign.keyPair.fromSecretKey(secretKey).publicKey);
}

// ── Phantom ───────────────────────────────────────────────────────────────────

interface PhantomProvider {
  isPhantom?: boolean;
  publicKey?: { toString(): string } | null;
  connect(): Promise<{ publicKey: { toString(): string } }>;
  disconnect?(): Promise<void>;
  signMessage(message: Uint8Array, display?: string): Promise<{ signature: Uint8Array }>;
  /** Event surface used by auth/walletWatcher.ts (accountChanged / disconnect). */
  on?(event: 'accountChanged' | 'disconnect', handler: (payload?: unknown) => void): void;
}

/** Return the injected Phantom provider, or null when the extension is absent. */
export function getPhantomProvider(): PhantomProvider | null {
  const anyWindow = globalThis as unknown as { solana?: PhantomProvider; phantom?: { solana?: PhantomProvider } };
  const provider = anyWindow.phantom?.solana ?? anyWindow.solana;
  return provider && provider.isPhantom ? provider : null;
}

export class PhantomWallet implements Wallet {
  readonly kind = 'phantom' as const;
  constructor(readonly address: string, private readonly provider: PhantomProvider) {}

  static async connect(): Promise<PhantomWallet> {
    const provider = getPhantomProvider();
    if (!provider) throw new Error('Phantom wallet not found. Install the Phantom browser extension.');
    const { publicKey } = await provider.connect();
    return new PhantomWallet(publicKey.toString(), provider);
  }

  async signMessage(message: string): Promise<string> {
    const bytes = new TextEncoder().encode(message);
    const { signature } = await this.provider.signMessage(bytes, 'utf8');
    return base58Encode(signature);
  }
}

// ── Dev throwaway keypair (DEV ONLY) ──────────────────────────────────────────

const DEV_KEY_STORAGE = 'gravelight.devWallet.secretKey';

export class DevKeypairWallet implements Wallet {
  readonly kind = 'dev' as const;
  constructor(readonly address: string, private readonly secretKey: Uint8Array) {}

  /** Load the persisted dev keypair from localStorage, or create + persist a new one. */
  static loadOrCreate(storage: Storage = localStorage): DevKeypairWallet {
    const stored = storage.getItem(DEV_KEY_STORAGE);
    if (stored) {
      try {
        const secretKey = base58Decode(stored);
        if (secretKey.length === nacl.sign.secretKeyLength) {
          return new DevKeypairWallet(deriveAddress(secretKey), secretKey);
        }
      } catch {
        // fall through to regenerate
      }
    }
    const keypair = nacl.sign.keyPair();
    storage.setItem(DEV_KEY_STORAGE, base58Encode(keypair.secretKey));
    return new DevKeypairWallet(base58Encode(keypair.publicKey), keypair.secretKey);
  }

  /** Forget the persisted dev keypair (so "new dev wallet" mints a fresh account). */
  static reset(storage: Storage = localStorage): void {
    storage.removeItem(DEV_KEY_STORAGE);
  }

  async signMessage(message: string): Promise<string> {
    return signMessageEd25519(message, this.secretKey);
  }
}
